import { CommonModule } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { SafeResourceUrl } from '@angular/platform-browser'
import { RouterModule } from '@angular/router'
import { SessionActivityType } from '@app/core/_models'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import { UserService } from '@app/core/_services/user.service'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import { SharedModule } from '@app/shared/shared.module'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import {
    ContentStackItem,
    ContentStackItemOptions,
    ContentStackService,
} from '@app/core/_services/content-stack.service'
import { ButtonModule } from 'primeng/button'

const NEW_USER = 'New!'
const ONE_MINUTE = 60000

@Component({
    selector: 'app-timeslot-partner-item',
    imports: [
        CommonModule,
        SharedModule,
        RouterModule,
        FontAwesomeModule,
        FmAvatarComponent,
        ButtonModule,
    ],
    templateUrl: './timeslot-partner-item.component.html',
    styleUrls: ['./timeslot-partner-item.component.scss'],
})
export class TimeslotPartnerItemComponent implements OnInit {
    @Input() displayName: string | null = null
    @Input() saved: boolean = false
    @Input() profileImage: string | null = null
    @Input() profileUrl: string | null = null
    @Input() totalSessions: number = 0
    @Input() sharedSessions: number = 0
    @Input() lastSessionTogether: number | null = null
    @Input() timezone: string | null = null
    @Input() isRandomCommunityMemberSlot: boolean = false
    @Input() snoozed: boolean = false
    @Input() snoozeTooltip: string | null = null
    @Input() selected: boolean = false
    @Input() activityType: SessionActivityType | null = null
    @Input() quietMode: boolean = false

    public sessionsInfo: string
    public lastMet: string
    public timezoneAndTime: string
    public profileLink: SafeResourceUrl
    private timeoutId: any

    constructor(
        private analyticsService: AnalyticsService,
        public userService: UserService,
        private dateFormat: UserDateFormattingService,
        private contentStackService: ContentStackService,
    ) {}

    ngOnInit(): void {
        this.buildSessionInfo()
        this.buildLastMetInfo()
        this.buildProfileLink()
        this.buildTimeAndZoneInfo()
    }

    buildSessionInfo() {
        if (!this.totalSessions) {
            this.sessionsInfo = NEW_USER
        } else if (this.totalSessions === 1) {
            this.sessionsInfo = '1 session'
        } else {
            this.sessionsInfo = `${this.totalSessions} sessions`
        }
        if (this.sharedSessions && this.sharedSessions > 0) {
            this.sessionsInfo = `${this.sessionsInfo} · ${this.sharedSessions} together`
        }
    }

    buildLastMetInfo() {
        if (this.lastSessionTogether) {
            this.lastMet = this.dateFormat.generateLastMetString(this.lastSessionTogether)
        }
    }

    buildProfileLink() {
        if (this.profileUrl) {
            this.profileLink = `/user/${this.profileUrl}`
        }
    }

    buildTimeAndZoneInfo() {
        if (this.timezone) {
            this.timezoneAndTime = `${this.timezone} · ${this.dateFormat.formatNow('h:mma', this.timezone)}`
        }
        this.trackTime()
    }

    private trackTime() {
        let now = new Date()
        let delay = ONE_MINUTE - now.getSeconds() * 1000

        this.timeoutId = setTimeout(() => {
            if (this.timezone) {
                this.buildTimeAndZoneInfo()
            }
        }, delay)
    }

    onUserProfileSelected() {
        this.analyticsService.logClickedEvent('User', 'Timeslot Partners: List Item')

        const options: ContentStackItemOptions = {
            popIfCovered: true,
            preserveStack: false,
        }

        this.contentStackService.pushProfileToDynamicPanel(this.profileUrl, options)
    }

    ngOnDestroy() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
    }
}
